import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { matchRoutes, useLocation } from 'react-router';

import type { RouteConfig } from '@shared/types/common.types';
import { flattenRoute } from '@shared/utils/routes';

import { ROUTES } from '@features/index';

/**
 * Finds and retrieves the display name of a particular path from global application
 * routes. This path should be a valid sub-path from the base URL domain of this application.
 *
 */
const useExtendedRouter = () => {
  const location = useLocation();

  const flattenedRoutes = useMemo(() => flattenRoute(ROUTES), []);

  const matchedPath = useMemo(
    () => matchRoutes(flattenedRoutes, location)?.[0]?.route ?? {},
    [flattenedRoutes, location]
  ) as RouteConfig;
  const {
    actionUrl,
    availableLanguages,
    backUrl,
    displayTitle,
    isHideBackNavButton,
    isLandingPage,
    isNavHeaderHide,
    layoutProps,
    path,
    titleTransKey,
    translationFile,
  } = matchedPath;
  const { t } = useTranslation(translationFile);

  const newDisplayTitle = titleTransKey ? t(titleTransKey) : displayTitle?.();
  const formattedDisplayTitle = newDisplayTitle?.replace(/<[^>]{0,1000}>/g, '');

  return {
    actionUrl,
    availableLanguages,
    backUrl,
    displayTitle: formattedDisplayTitle,
    isHideBackNavButton,
    isLandingPage,
    isNavHeaderHide,
    layoutProps,
    path,
    titleTransKey,
    translationFile,
  };
};

export { useExtendedRouter };
